import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';

interface LgSection {
  number: string;
  title: string;
  paragraphs?: string[];
  bullets?: string[];
  subheading?: string;
}

const PRIVACY_SECTIONS: LgSection[] = [
  {
    number: '1',
    title: 'Introduction',
    paragraphs: [
      'Sehyog Academy ("Sehyog", "we", "us" or "our") is committed to protecting the privacy of everyone who uses our website and services, including parents, guardians, and the students we tutor. This Privacy Policy explains what personal information we collect, how we use it, who we share it with, and the rights you have over it.',
      'This policy applies to sehyog.co.uk, our booking and enrolment systems, and any tuition, mock examination, or workshop service we provide, whether online or face-to-face. Because much of what we do involves supporting children, we have paid particular attention to how we handle children\'s information throughout this policy.'
    ]
  },
  {
    number: '2',
    title: 'Who Is Responsible for Your Data',
    paragraphs: [
      'Sehyog Academy is the data controller for the personal information described in this policy, meaning we decide how and why that information is used.'
    ]
  },
  {
    number: '3',
    title: 'Information We Collect',
    paragraphs: [
      'Depending on how you interact with us, we may collect the following categories of information:'
    ],
    subheading: 'From parents and guardians',
    bullets: [
      'Name, email address, phone number and postal address',
      'Payment and billing details (processed by our payment provider — we do not store full card numbers)',
      'Booking history, preferences, and correspondence with our team',
      'Communications sent to us by email, phone, WhatsApp, or through our website\'s chat widget'
    ]
  },
  {
    number: '3a',
    title: '',
    subheading: 'About the student',
    bullets: [
      'First name, last name, date of birth or year group, and current school',
      'Exam board(s), target schools, and academic year of the relevant assessment (11+, GCSE, A-Level)',
      'Attendance records for tuition sessions, mock tests, and workshops',
      'Test scores, standardised scores, rankings, percentiles, and the resulting Result Analysis Reports (RAR)',
      'Tutor feedback notes, areas of strength and areas for improvement, recorded after tuition sessions'
    ]
  },
  {
    number: '3b',
    title: '',
    subheading: 'Collected automatically when you use our website',
    bullets: [
      'Device and browser information, IP address, and approximate location',
      'Pages visited, time spent on our site, and how you arrived at sehyog.co.uk',
      'Cookie identifiers and similar tracking technologies (see Section 8, Cookies)'
    ],
    paragraphs: [
      'We do not knowingly collect sensitive categories of personal data (such as health information) unless you choose to share it with us — for example, to let us know about a learning need or access requirement so that we can make reasonable adjustments for a session.'
    ]
  },
  {
    number: '4',
    title: 'How We Collect Your Information',
    bullets: [
      'Directly from you, when you book a session, register for an account, fill in a contact form, or speak with us by phone, email, or WhatsApp',
      'Automatically, through cookies and similar technologies when you browse our website',
      'From a school, consortium, or referral partner, where they have passed on your details with your consent — for example, following a school-organised information session'
    ]
  },
  {
    number: '5',
    title: 'How We Use Your Information',
    paragraphs: ['We use the personal information we collect to:'],
    bullets: [
      'Process bookings, payments, and enrolments, and to confirm session details',
      'Deliver tuition, mock examinations, and workshops, and to produce Result Analysis Reports and cohort benchmarking',
      'Communicate with you about upcoming sessions, schedule changes, and account matters',
      'Provide customer support and respond to enquiries, including through our WhatsApp chat',
      'Send marketing communications about courses, bootcamps, and offers, where you have agreed to receive them (see Section 11)',
      'Maintain the security of our website and prevent fraud or misuse',
      'Comply with our legal, accounting, and regulatory obligations'
    ]
  },
  {
    number: '6',
    title: 'Our Legal Basis for Processing',
    paragraphs: ['Under UK data protection law, we rely on the following legal bases to process your personal information:'],
    bullets: [
      '<b>Performance of a contract</b> — to take a booking, deliver a tuition programme or mock test, and process payment',
      '<b>Legitimate interests</b> — to improve our services, keep our website secure, and understand how our courses are performing, in a way that does not override your rights',
      '<b>Consent</b> — for marketing communications and for certain cookies, which you can withdraw at any time',
      '<b>Legal obligation</b> — to meet our obligations relating to tax, accounting, and, where relevant, safeguarding'
    ]
  },
  {
    number: '7',
    title: 'Children\'s Information and Parental Consent',
    paragraphs: [
      'Because our services are designed for students from Year 3 through to Year 13, we collect information about children as a normal part of providing tuition and assessment. A parent or legal guardian is always the one who creates the booking or account, and is treated as the point of contact and the party who consents to their child\'s information being processed for the purposes described in this policy.',
      'We keep the amount of information we collect about a child proportionate to what is needed to deliver tuition and produce accurate performance reporting. We do not use children\'s data for marketing purposes, and we do not knowingly allow a child to set up their own account or provide consent independently of a parent or guardian.',
      'If you believe a child has provided us with personal information without appropriate parental consent, please contact us so that we can review and, where necessary, delete it.'
    ]
  },
  {
    number: '8',
    title: 'Cookies and Similar Technologies',
    paragraphs: [
      'Our website uses cookies and similar technologies to make the site work properly, remember your preferences, and help us understand how the site is used. When you first visit sehyog.co.uk, you will be asked to agree to our use of cookies.'
    ],
    bullets: [
      '<b>Strictly necessary cookies</b> — required for core site functions such as navigation and the booking process; these cannot be switched off',
      '<b>Performance and analytics cookies</b> — help us understand how visitors use our site so we can improve it',
      '<b>Functionality cookies</b> — remember choices you have made, such as your selected centre or exam board filter',
      '<b>Third-party and chat cookies</b> — used by embedded tools such as our WhatsApp chat widget'
    ]
  },
  {
    number: '9',
    title: 'Who We Share Your Information With',
    paragraphs: ['We share personal information only where necessary, and always with appropriate safeguards in place:'],
    bullets: [
      '<b>Tutors and centre staff</b> delivering the specific session a student is booked onto, limited to the information they need to teach and assess the student',
      '<b>Payment processors</b>, to securely handle card payments; we do not store full payment card details ourselves',
      '<b>IT and website service providers</b> who host our website, booking system, and email communications on our behalf',
      '<b>Communication platforms</b> such as WhatsApp, where you choose to contact us or receive updates through that channel',
      '<b>Venue partners</b>, where a face-to-face session takes place at a partner school or centre, limited to attendance and safeguarding-relevant information',
      '<b>Regulators, examination bodies, or authorities</b>, where we are required to do so by law'
    ]
  },
  {
    number: '9a',
    title: '',
    paragraphs: [
      'We do not sell personal information to third parties. Where a third-party provider processes data on our behalf, we have contracts in place requiring them to protect it to a standard consistent with UK data protection law.'
    ]
  },
  {
    number: '10',
    title: 'International Transfers',
    paragraphs: [
      'Some of the service providers we use to run our website, booking system, and communication tools may store or process information outside the UK. Where this happens, we take steps to ensure your information continues to receive an appropriate level of protection, including through the use of UK-approved transfer mechanisms such as Standard Contractual Clauses or an equivalent safeguard recognised under UK data protection law.'
    ]
  },
  {
    number: '11',
    title: 'Marketing Communications',
    paragraphs: [
      'With your consent, we may send you information about new courses, bootcamps, mock test dates, and offers by email, SMS, or WhatsApp. You can opt out of marketing communications at any time by using the unsubscribe link in our emails, replying "STOP" to a text message, or contacting us directly. Opting out of marketing will not affect essential communications about a session you have already booked.'
    ]
  },
  {
    number: '12',
    title: 'How Long We Keep Your Information',
    paragraphs: ['We keep personal information for as long as is necessary to provide our services and to meet our legal and accounting obligations. As a general guide:'],
    bullets: [
      'Booking, payment, and invoicing records are typically kept for up to six years, in line with UK tax and accounting requirements',
      'Student attendance, test scores, and Result Analysis Reports are typically kept for the duration of a student\'s enrolment with us and for a reasonable period afterwards, so that historical progress reports remain available on request',
      'Marketing consent records are kept until you withdraw your consent or, if earlier, in line with our standard retention practice'
    ]
  },
  {
    number: '12a',
    title: '',
    paragraphs: ['Where information is no longer needed, we securely delete or anonymise it.']
  },
  {
    number: '13',
    title: 'Your Rights',
    paragraphs: [
      'Under UK data protection law, you have a number of rights in relation to your personal information and, where you are the parent or guardian, your child\'s information. These include the right to:'
    ],
    bullets: [
      'Be informed about how your data is used (as set out in this policy)',
      'Request a copy of the personal information we hold about you or your child',
      'Ask us to correct information that is inaccurate or incomplete',
      'Ask us to delete your information, in certain circumstances',
      'Ask us to restrict or object to certain processing',
      'Request that certain information be transferred to another organisation, in a portable format',
      'Withdraw consent at any time, where we rely on consent as our legal basis'
    ]
  },
  {
    number: '13a',
    title: '',
    paragraphs: [
      'We may need to verify your identity, and your relationship to a child, before responding to a request. We will respond within the timeframes required by law, and normally within one month.',
      'If you are not satisfied with how we have handled your information, you have the right to complain to the UK\'s supervisory authority, the Information Commissioner\'s Office (ICO), at ico.org.uk or by calling 0303 123 1113.'
    ]
  },
  {
    number: '14',
    title: 'Keeping Your Information Secure',
    paragraphs: [
      'We use appropriate technical and organisational measures to protect the personal information we hold, including access controls, secure payment processing, and restricting staff access to student information to those who need it to deliver our services. While we take reasonable steps to protect your information, no method of transmission over the internet or electronic storage is completely secure, and we cannot guarantee absolute security.'
    ]
  },
  {
    number: '15',
    title: 'Changes to This Policy',
    paragraphs: [
      'We may update this Privacy Policy from time to time to reflect changes in our services or in data protection law. Where we make material changes, we will update the "Effective date" shown at the top of this page. We encourage you to review this policy periodically.'
    ]
  }
];

@Component({
  selector: 'app-privacy-policy',
  imports: [CommonModule],
  templateUrl: './privacy-policy.component.html',
  styleUrl: './privacy-policy.component.css',
})
export class PrivacyPolicyComponent {
  effectiveDate = '22 August 2026';
  sections: LgSection[] = PRIVACY_SECTIONS;

  ngOnInit(): void {
    this.loadFonts();
  }

  private loadFonts(): void {
    var self = this;
    var fontId = 'lg-legal-fonts';
    if (document.getElementById(fontId)) {
      return;
    }
    var link = document.createElement('link');
    link.id = fontId;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Nunito:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
  }
}
